import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { ExtractedDocumentData } from '@/hooks/useDocumentExtraction';
import { format } from 'date-fns';

interface DocumentExtractionConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extractedData: ExtractedDocumentData;
  onConfirm: (data: ExtractedDocumentData) => void;
  onManualEntry: () => void;
  isSubmitting?: boolean;
  showDateFields?: boolean;
}

export function DocumentExtractionConfirmDialog({
  open,
  onOpenChange,
  extractedData,
  onConfirm,
  onManualEntry,
  isSubmitting = false,
  showDateFields = true
}: DocumentExtractionConfirmDialogProps) {
  const [editedData, setEditedData] = useState<ExtractedDocumentData>(extractedData);

  const confidence = extractedData.confidence;
  const confidenceColor = confidence >= 0.8 ? 'success' : confidence >= 0.5 ? 'warning' : 'destructive';
  const confidenceLabel = confidence >= 0.8 ? 'High Confidence' : confidence >= 0.5 ? 'Medium Confidence' : 'Low Confidence';
  const ConfidenceIcon = confidence >= 0.8 ? CheckCircle2 : AlertCircle;

  const handleFieldChange = (field: keyof ExtractedDocumentData, value: any) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleConfirm = () => {
    onConfirm(editedData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Review Extracted Document Data</DialogTitle>
            <Badge variant={confidenceColor as any} className="flex items-center gap-1">
              <ConfidenceIcon className="h-3 w-3" />
              {confidenceLabel} ({Math.round(confidence * 100)}%)
            </Badge>
          </div>
          <DialogDescription>
            {confidence >= 0.8 
              ? 'AI extracted the following information with high confidence. Please review and edit if needed.'
              : confidence >= 0.5
              ? 'Please carefully review all extracted fields below and correct any errors.'
              : 'Extraction confidence is low. Please verify all information or use manual entry.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="document_number">
                Document Number
                {!editedData.document_number && <span className="text-destructive ml-1">*</span>}
              </Label>
              <Input
                id="document_number"
                value={editedData.document_number || ''}
                onChange={(e) => handleFieldChange('document_number', e.target.value)}
                placeholder="Enter document number"
                className={!editedData.document_number ? 'border-destructive' : ''}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="holder_name">Holder Name</Label>
              <Input
                id="holder_name"
                value={editedData.holder_name || ''}
                onChange={(e) => handleFieldChange('holder_name', e.target.value)}
                placeholder="Enter holder name"
              />
            </div>
          </div>

          {showDateFields && (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="issue_date">Issue Date</Label>
                <Input
                  id="issue_date"
                  type="date"
                  value={editedData.issue_date || ''}
                  onChange={(e) => handleFieldChange('issue_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiry_date">Expiry Date</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={editedData.expiry_date || ''}
                  onChange={(e) => handleFieldChange('expiry_date', e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="issuing_authority">Issuing Authority</Label>
            <Input
              id="issuing_authority"
              value={editedData.issuing_authority || ''}
              onChange={(e) => handleFieldChange('issuing_authority', e.target.value)}
              placeholder="Enter issuing authority"
            />
          </div>

          {editedData.coverage_type && (
            <div className="space-y-2">
              <Label htmlFor="coverage_type">Coverage Type</Label>
              <Input
                id="coverage_type"
                value={editedData.coverage_type || ''}
                onChange={(e) => handleFieldChange('coverage_type', e.target.value)}
                placeholder="Enter coverage type"
              />
            </div>
          )}

          {editedData.coverage_amount && (
            <div className="space-y-2">
              <Label htmlFor="coverage_amount">Coverage Amount</Label>
              <Input
                id="coverage_amount"
                value={editedData.coverage_amount || ''}
                onChange={(e) => handleFieldChange('coverage_amount', e.target.value)}
                placeholder="Enter coverage amount"
              />
            </div>
          )}

          {editedData.additional_info && Object.keys(editedData.additional_info).length > 0 && (
            <div className="space-y-2">
              <Label>Additional Information</Label>
              <Textarea
                value={JSON.stringify(editedData.additional_info, null, 2)}
                onChange={(e) => {
                  try {
                    handleFieldChange('additional_info', JSON.parse(e.target.value));
                  } catch (err) {
                    // Invalid JSON, ignore
                  }
                }}
                rows={4}
                className="font-mono text-xs"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onManualEntry}
            disabled={isSubmitting}
          >
            Manual Entry
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || !editedData.document_number}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm & Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
